// The editor's changes to one guide, as plain functions (each returns a new guide). Steps and pictures are moved, added and
// removed by position, but a step keeps pointing at its picture by the picture's id, so none of this changes which picture
// belongs to which step, except removing a picture, which unlinks the steps that used it.
import type { Guide, Step } from './steps';

const newId = (prefix: string) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const out = list.slice();
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
}

export const moveStep = (g: Guide, from: number, to: number): Guide => ({ ...g, steps: move(g.steps, from, to) });
export const moveImage = (g: Guide, from: number, to: number): Guide => ({ ...g, images: move(g.images, from, to) });

// A new, empty step at `at` (0 = first; steps.length = last).
export const insertStep = (g: Guide, at: number): Guide => {
  const steps = g.steps.slice();
  steps.splice(Math.max(0, Math.min(at, steps.length)), 0, { id: newId('s'), text: '' });
  return { ...g, steps };
};
export const removeStep = (g: Guide, at: number): Guide => ({ ...g, steps: g.steps.filter((_, i) => i !== at) });
export const updateStep = (g: Guide, at: number, patch: Partial<Omit<Step, 'id'>>): Guide =>
  ({ ...g, steps: g.steps.map((s, i) => (i === at ? { ...s, ...patch } : s)) });
export const linkImage = (g: Guide, at: number, image: string | undefined): Guide =>
  ({ ...g, steps: g.steps.map((s, i) => { if (i !== at) return s; const { image: _old, ...rest } = s; return image ? { ...rest, image } : rest; }) });

export const addImages = (g: Guide, ids: string[]): Guide => ({ ...g, images: [...g.images, ...ids.map((id) => ({ id, caption: '' }))] });
export const captionImage = (g: Guide, at: number, caption: string): Guide =>
  ({ ...g, images: g.images.map((im, i) => (i === at ? { ...im, caption } : im)) });
// Removes a picture from the gallery and unlinks it from its steps. (The file itself stays on the server.)
export const removeImage = (g: Guide, at: number): Guide => {
  const gone = g.images[at]?.id;
  return {
    ...g,
    images: g.images.filter((_, i) => i !== at),
    steps: g.steps.map((s) => { if (s.image !== gone) return s; const { image: _old, ...rest } = s; return rest; }),
  };
};

// Which steps (by number, from 1) show a picture.
export const stepsUsing = (g: Guide, imageId: string) => g.steps.flatMap((s, i) => (s.image === imageId ? [i + 1] : []));

// A download button as typed: no address means no button; an address without https:// gets it; no text gets "Download".
const tidyLink = (l: Guide['link']): Guide['link'] => {
  const url = l?.url.trim() ?? '';
  if (!url) return null;
  return { label: l!.label.trim() || 'Download', url: /^https?:\/\//i.test(url) ? url : `https://${url}` };
};

// The text a person typed, trimmed the way the server keeps it; "" detail is dropped.
export const tidy = (g: Guide): Guide => ({
  ...g,
  tab: g.tab.trim(), title: g.title.trim(), intro: g.intro.trim(), link: tidyLink(g.link),
  images: g.images.map((im) => ({ ...im, caption: im.caption.trim() })),
  steps: g.steps.map(({ detail, ...s }) => ({ ...s, text: s.text.trim(), ...(detail?.trim() ? { detail: detail.trim() } : {}) })),
});

// ---- the guides themselves (the tabs) ----

export const MAX_GUIDES = 10; // the server takes at most 10

// A new guide with one empty step, added after the others. Returns the new list and the new guide's id.
export const addGuide = (list: Guide[]): { list: Guide[]; id: string } => {
  const id = newId('g');
  const n = list.length + 1;
  return { list: [...list, { id, tab: `Guide ${n}`, title: `Guide ${n}`, intro: '', link: null, steps: [{ id: newId('s'), text: '' }], images: [] }], id };
};
export const removeGuide = (list: Guide[], id: string): Guide[] => (list.length > 1 ? list.filter((g) => g.id !== id) : list);
export const moveGuide = (list: Guide[], id: string, by: -1 | 1): Guide[] => {
  const i = list.findIndex((g) => g.id === id);
  return i < 0 ? list : move(list, i, i + by);
};
